/**
 * COD Product Page Functionality
 * Reads all variant data embedded in the page (no AJAX needed).
 * Shows variant selection modal when needed, then goes to COD checkout.
 */

// A policy exists only when at least one variant is explicitly retired.
function homieSalePolicy() {
  const picker = document.querySelector('variant-picker[data-sale-managed="true"]');
  if (!picker) return null;
  try {
    const policy = JSON.parse(picker.querySelector('[data-homie-sale-policy]').textContent);
    if (!policy || !Array.isArray(policy.variants)) return { variants: [] };
    return policy;
  }
  catch (_) { return { variants: [] }; } // marked + unreadable = fail closed
}
function homieSaleAllowed(id) {
  const policy = homieSalePolicy();
  if (!policy) return true;
  return policy.variants.some(v => String(v.id) === String(id) && !v.saleDisabled && v.available);
}
function homieSaleNotice() {
  const picker = document.querySelector('variant-picker[data-sale-managed="true"]');
  if (!picker) return;
  let notice = document.getElementById('hs-sale-retired-notice');
  if (!notice) {
    notice = document.createElement('p');
    notice.id = 'hs-sale-retired-notice';
    notice.className = 'hs-hv-hint';
    notice.setAttribute('role', 'alert');
    picker.insertAdjacentElement('beforebegin', notice);
  }
  notice.textContent = '此規格已停售，請選擇其他規格；停售規格不會加入購物車。';
}
// M73: option-order availability for the variant modal (sale-managed products only; same rule as variant-main-picker).
function hsComboModal(modal, selectedValues, selectedVids) {
  const policy = homieSalePolicy();
  if (!policy) return null;
  const active = policy.variants.filter(v => !v.saleDisabled && Array.isArray(v.options));
  const buttons = Array.from(modal.querySelectorAll('.opt'));
  const order = [...new Set(buttons.map(b => Number(b.dataset.g)))].sort((a, b) => a - b);
  const chosen = () => Object.keys(selectedValues).map(Number);
  const refresh = () => {
    buttons.forEach(btn => {
      const g = Number(btn.dataset.g);
      const ok = active.some(v => v.options[g] === btn.dataset.val &&
        chosen().every(i => i >= g || v.options[i] === selectedValues[i]));
      if (ok) btn.removeAttribute('disabled'); else btn.setAttribute('disabled', '');
      btn.classList.toggle('on', selectedValues[g] === btn.dataset.val);
    });
  };
  const after = g => {
    const hint = modal.querySelector('#cvs-combo-hint');
    if (active.some(v => chosen().every(i => v.options[i] === selectedValues[i]))) {
      if (hint) hint.remove();
      refresh();
      return;
    }
    const pool = active.filter(v => v.options[g] === selectedValues[g]);
    const candidates = pool.some(v => v.available) ? pool.filter(v => v.available) : pool;
    if (candidates.length) {
      const score = v => order.map(i => (i !== g && v.options[i] === selectedValues[i] ? '1' : '0')).join('');
      const best = candidates.reduce((a, b) => (score(b) > score(a) ? b : a));
      chosen().forEach(i => {
        if (i === g) return;
        selectedValues[i] = best.options[i];
        const btn = buttons.find(b => Number(b.dataset.g) === i && b.dataset.val === best.options[i]);
        if (btn && btn.dataset.vid) selectedVids[i] = btn.dataset.vid;
      });
      const note = hint || document.createElement('p');
      if (!hint) {
        note.id = 'cvs-combo-hint';
        note.className = 'hint';
        note.setAttribute('role', 'status');
        modal.querySelector('.ft').insertAdjacentElement('afterbegin', note);
      }
      note.textContent = '這個組合目前沒有販售，已幫您改成可購買的規格。';
    }
    refresh();
  };
  return { refresh, after };
}
function homieActiveVariants(variants) {
  const policy = homieSalePolicy();
  return policy ? variants.filter(v => policy.variants.some(p => String(p.id) === String(v.id) && !p.saleDisabled)) : variants;
}
let _variants = null; // 全部 variant 資料（從頁面 JSON 直接讀取）
let _product = null;  // 商品基本資料

// ─── 從頁面讀取所有 variant 資料 ─────────────────────────────────────────────
function loadVariants() {
  if (_variants) return _variants;

  // 方法 1：讀取我們在 Liquid 模板中嵌入的 JSON（最可靠）
  const variantScript = document.querySelector('script[id^="cod-variants-"]');
  if (variantScript) {
    try {
      _variants = homieActiveVariants(JSON.parse(variantScript.textContent));
      if (_variants?.length) {
        console.log(`[COD] Loaded ${_variants.length} variants from embedded JSON`);
        return _variants;
      }
    } catch (_) {}
  }

  // 方法 2：從 window.ShopifyAnalytics 取
  const p = window.ShopifyAnalytics?.meta?.product || window.product;
  if (p?.variants?.length) {
    _variants = homieActiveVariants(p.variants);
    _product = p;
    console.log(`[COD] Loaded ${_variants.length} variants from ShopifyAnalytics`);
    return _variants;
  }

  // 方法 3：從頁面其他 JSON script 取
  for (const sc of document.querySelectorAll('script[type="application/json"]')) {
    try {
      const d = JSON.parse(sc.textContent);
      if (Array.isArray(d) && d.length && d[0]?.variant_id !== undefined) {
        // 這是 variant 陣列
        _variants = homieActiveVariants(d);
        return _variants;
      }
      if (d?.variants?.length) {
        _variants = homieActiveVariants(d.variants);
        _product = d;
        return _variants;
      }
    } catch (_) {}
  }

  console.warn('[COD] Could not load variants from page');
  return null;
}

// ─── 根據已選 option values 找到 variant ──────────────────────────────────────
function findVariantByValues(selectedValuesByGroup) {
  const variants = loadVariants();
  if (!variants) return null;

  const values = Object.keys(selectedValuesByGroup)
    .sort((a, b) => Number(a) - Number(b))
    .map(k => selectedValuesByGroup[k])
    .filter(Boolean);

  if (!values.length) return null;

  // 嚴格匹配：所有 option 都要完全一樣
  return variants.find(v => {
    const opts = [v.option1, v.option2, v.option3].filter(Boolean);
    return opts.length === values.length && values.every(val => opts.includes(val));
  }) || variants.find(v => {
    // 寬鬆匹配：只要選的 values 都包含在 variant options 中
    const opts = [v.option1, v.option2, v.option3].filter(Boolean);
    return values.every(val => opts.includes(val));
  }) || null;
}

// ─── R7 (2026-05-13): /cart/add.js retry helper(最多 3 次,指數 backoff)──────
//   修客戶體驗 bug:第一次 add 失敗 → 紅字 + 不跳轉 → 客戶要再點一次
//   策略:retry 3 次,每次間隔 attempt*300ms;全部失敗也回傳 success:false 不 throw
async function addToCartWithRetry(variantId, quantity, maxAttempts = 3) {
  if (!homieSaleAllowed(variantId)) {
    homieSaleNotice();
    return { success: false, saleDisabled: true, attempt: 0 };
  }
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: quantity || 1 })
      });
      if (addRes.ok) return { success: true, attempt, status: addRes.status };
      console.warn(`[COD] add.js attempt ${attempt}/${maxAttempts} HTTP ${addRes.status}`);
      if (attempt < maxAttempts) await new Promise(r => setTimeout(r, attempt * 300));
      else return { success: false, attempt, status: addRes.status };
    } catch (err) {
      console.warn(`[COD] add.js attempt ${attempt}/${maxAttempts} network error:`, err);
      if (attempt < maxAttempts) await new Promise(r => setTimeout(r, attempt * 300));
      else return { success: false, attempt, error: err.message };
    }
  }
  return { success: false, attempt: maxAttempts };
}

// ─── 前往 COD 結帳頁（先加入 Shopify 購物車，再跳轉） ───────────────────────
async function goToCheckout(variant, quantity, productInfo, confirmBtn) {
  if (!homieSaleAllowed(variant && variant.id)) { homieSaleNotice(); return; }
  if (confirmBtn) {
    confirmBtn.disabled = true;
    confirmBtn.textContent = '加入購物車中...';
  }

  try {
    // ── TD-33 (2026-05-11): 立即下單前 GET /cart.js 檢查 ──────────────────
    //   修「先加入購物車 → 再立即下單 → cart 變 2」UX bug
    //   若該 variant 已在 cart → skip add（也 skip HomieTrack 防 Pixel 灌水）
    //   若該 variant 不在 cart → 走原 P0h 疊加流程加進去
    //   GET /cart.js 失敗 → fallback 到原 add 行為（R5：不卡用戶）
    let alreadyInCart = false;
    try {
      const cartRes = await fetch('/cart.js', { cache: 'no-store' });
      if (cartRes.ok) {
        const cart = await cartRes.json();
        const targetVid = parseInt(variant.id, 10);
        alreadyInCart = (cart.items || []).some(it => Number(it.variant_id) === targetVid);
      }
    } catch (e) {
      console.warn('[TD-33] GET /cart.js 失敗，fallback 走原 add 行為:', e);
    }

    if (!alreadyInCart) {
      // P0h (2026-05-06): 策略 1「疊加」— 不清空 cart，新商品加進現有 cart 一起結帳
      //   業務面 Tesla 確認：加入購物車=疊加+不跳轉、立即下單=疊加+跳 cod-checkout
      // R7 (2026-05-13): 改用 addToCartWithRetry(3 次 retry);失敗也繼續跳轉,
      //   交給 cod-checkout 頁面 self-heal 補加(避免「卡在商品頁」UX)
      const addResult = await addToCartWithRetry(variant.id, quantity, 3);

      if (addResult.success) {
        console.log(`[COD] add.js attempt ${addResult.attempt}/3 ✅`);

        // Stage 2E-1 (2026-05-07): 真實 AddToCart hook
        //   走 window.HomieTrack.addToCart → /api/track-event(fbCapi 系統)
        //   variant.price 是 cents，÷100 變元
        //   TD-33 (2026-05-11): 只在真的 add 時 fire(R6:跳過 add 時不能灌水 Pixel)
        // TD-35 (2026-05-13): title 4 層 fallback 對齊 homie-track.js L189
        try {
          if (window.HomieTrack && window.HomieTrack.addToCart) {
            window.HomieTrack.addToCart({
              variant_id: variant.id,
              product_id: (productInfo && productInfo.id) || (_product && _product.id),
              title: (productInfo && productInfo.title)
                  || (_product && _product.title)
                  || (document.querySelector('[data-product-title]')?.dataset?.productTitle)
                  || (document.querySelector('h1.product-title, h1.product__title, .product-single__title')?.textContent?.trim())
                  || '',
              price: Number(variant.price) / 100,
              quantity: quantity || 1,
              source: 'product-page'
            });
          }
        } catch (_) { /* 不阻擋下單 */ }
      } else {
        // R7: 全部 retry 失敗,仍繼續跳轉,讓 cod-checkout self-heal 補加
        //   注意:此情況不 fire HomieTrack(避免 Pixel 灌水);self-heal 成功後由 cart 漏斗補
        console.warn('[COD] add.js 全部 retry 失敗,仍跳轉 cod-checkout 由 self-heal 接手', addResult);
      }
    } else {
      console.log('[TD-33] variant ' + variant.id + ' 已在 cart，跳過 add 直接跳轉 cod-checkout');
    }

    // 3. P0f (2026-05-02): 判定是否為 No-Store-Pickup variant，帶 ?nsp=1 給 cod-checkout
    //    SKU fallback 反查：若 variant 物件本身沒帶 sku（直接買按鈕 fallback / 單規格商品 fallback），
    //    從 loadVariants() in-memory 反查補回。loadVariants() 來自 variant-main-picker.liquid
    //    `{{ product.variants | json }}` 含 sku 欄位。
    let sku = variant && variant.sku;
    if (!sku && variant && variant.id) {
      try {
        const all = (typeof loadVariants === 'function') ? loadVariants() : null;
        const found = all && all.find(v => String(v.id) === String(variant.id));
        if (found && found.sku) sku = found.sku;
      } catch (_) {}
    }
    const isNsp = !!(sku && window.COD_NSP && window.COD_NSP.isBlocked(sku));
    // R7 (2026-05-13): 帶 ensure_vid + ensure_qty 給 cod-checkout self-heal 使用
    const ensureQs = `ensure_vid=${encodeURIComponent(variant.id)}&ensure_qty=${encodeURIComponent(quantity || 1)}`;
    const targetUrl = isNsp
      ? `/pages/cod-checkout?nsp=1&${ensureQs}`
      : `/pages/cod-checkout?${ensureQs}`;
    if (isNsp) console.log('[COD_NSP] product entry → blocked SKU "' + sku + '" → ' + targetUrl);
    window.location.href = targetUrl;

  } catch (err) {
    // R7 (2026-05-13): 此 catch 只接 JavaScript 自身 bug(addToCartWithRetry 不 throw,
    //   /cart.js GET 已 try/catch);文案改一般化「系統忙碌」
    console.error('[COD] goToCheckout JS error:', err);
    if (confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.textContent = '選好了，前往下單';
    }
    const hint = document.getElementById('cvs-hint');
    if (hint) {
      hint.textContent = '⚠ 系統忙碌，請稍候再試';
      hint.className = 'hint err';
    }
  }
}

// ─── 加入購物車（留在頁面，不跳轉）──────────────────────────────────────────────
//   給「加入購物車」原生鈕在「未選規格 → 跳彈窗選好」後使用。
//   與「立即下單」同一套：自家 fetch /cart/add.js(addToCartWithRetry)+ 手動發一次
//   HomieTrack.addToCart（走自家 fetch 不觸發主題事件 → Pixel 不會雙發）；
//   再 dispatch 'cart:refresh' 讓 floating-cart 自行刷新（homie-track 不監聽此事件 →
//   不會二次觸發 Pixel）。全程不跳轉、不動 floating-cart.js 本體。
async function addToCartStayOnPage(variant, quantity, productInfo, confirmBtn) {
  if (!homieSaleAllowed(variant && variant.id)) { homieSaleNotice(); return; }
  if (confirmBtn) {
    confirmBtn.disabled = true;
    confirmBtn.textContent = '加入購物車中...';
  }

  const addResult = await addToCartWithRetry(variant.id, quantity, 3);

  if (addResult.success) {
    console.log(`[COD] add-to-cart attempt ${addResult.attempt}/3 ✅`);
    // Pixel：手動觸發一次（對齊 goToCheckout 的 HomieTrack.addToCart 寫法）
    try {
      // 價格兜底：單規格 fallback variant 可能沒帶 price → 從 loadVariants 反查，避免 Pixel value=0
      let pixelPrice = Number(variant.price);
      if (!isFinite(pixelPrice)) {
        const full = (loadVariants() || []).find(v => String(v.id) === String(variant.id));
        pixelPrice = full ? Number(full.price) : NaN;
      }
      if (window.HomieTrack && window.HomieTrack.addToCart) {
        window.HomieTrack.addToCart({
          variant_id: variant.id,
          product_id: (productInfo && productInfo.id) || (_product && _product.id),
          title: (productInfo && productInfo.title)
              || (_product && _product.title)
              || document.querySelector('[data-product-title]')?.dataset?.productTitle
              || document.querySelector('h1.product-title, h1.product__title, .product-single__title')?.textContent?.trim()
              || '',
          price: isFinite(pixelPrice) ? pixelPrice / 100 : 0,
          quantity: quantity || 1,
          source: 'product-page-add'
        });
      }
    } catch (_) { /* 不阻擋加購 */ }

    // 通知 floating-cart 刷新（它監聽 'cart:refresh'；homie-track 不監聽 → 不雙發 Pixel）
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'cod-product' } }));
    } catch (_) {}

    // 關閉彈窗，留在商品頁
    document.getElementById('cvs-modal')?.remove();
  } else {
    // 全部 retry 失敗：留在彈窗顯示提示，不跳转
    console.warn('[COD] add-to-cart 全部 retry 失敗', addResult);
    if (confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.textContent = '加入購物車';
    }
    const hint = document.getElementById('cvs-hint');
    if (hint) {
      hint.textContent = '⚠ 加入購物車失敗，請再試一次';
      hint.className = 'hint err';
    }
  }
}

// ─── 注入彈窗樣式 ────────────────────────────────────────────────────────────
function injectStyles() {
  if (document.getElementById('cvs-styles')) return;
  const s = document.createElement('style');
  s.id = 'cvs-styles';
  s.textContent = `
    #cvs-modal {
      position:fixed; inset:0; z-index:999999;
      display:flex; align-items:flex-end; justify-content:center;
      background:rgba(0,0,0,0.5);
    }
    @media(min-width:600px){#cvs-modal{align-items:center;}}
    #cvs-modal .box{
      background:#fff; border-radius:16px 16px 0 0;
      width:100%; max-width:500px; max-height:88vh; overflow-y:auto;
      box-shadow:0 -6px 40px rgba(0,0,0,0.18); animation:cvsUp .25s ease;
    }
    @media(min-width:600px){#cvs-modal .box{border-radius:14px;animation:cvsZoom .2s ease;}}
    @keyframes cvsUp  {from{transform:translateY(28px);opacity:0}to{transform:none;opacity:1}}
    @keyframes cvsZoom{from{transform:scale(.95);opacity:0}to{transform:none;opacity:1}}
    #cvs-modal .hd{
      display:flex;align-items:center;justify-content:space-between;
      padding:18px 20px 14px;border-bottom:1px solid #f0f0f0;
      position:sticky;top:0;background:#fff;z-index:1;
    }
    #cvs-modal .hd-title{font-size:16px;font-weight:600;color:#111;}
    #cvs-modal .hd-close{
      background:none;border:none;cursor:pointer;
      font-size:20px;color:#888;line-height:1;padding:4px 6px;border-radius:50%;
    }
    #cvs-modal .hd-close:hover{background:#f5f5f5;}
    #cvs-modal .bd{padding:16px 20px;}
    #cvs-modal .grp{margin-bottom:18px;}
    #cvs-modal .grp:last-child{margin-bottom:0;}
    #cvs-modal .grp-lbl{font-size:13px;font-weight:500;color:#555;margin:0 0 10px;}
    #cvs-modal .opts{display:flex;flex-wrap:wrap;gap:8px;}
    #cvs-modal .opt{
      padding:8px 16px;border:1.5px solid #d0d0d0;border-radius:6px;
      background:#fff;cursor:pointer;font-size:14px;color:#333;
      transition:border-color .12s,background .12s,color .12s;user-select:none;
    }
    #cvs-modal .opt:hover:not([disabled]){border-color:#888;}
    #cvs-modal .opt.on{border-color:#111;background:#111;color:#fff;}
    #cvs-modal .opt[disabled]{opacity:.35;cursor:not-allowed;text-decoration:line-through;}
    #cvs-modal .ft{
      padding:14px 20px 24px;border-top:1px solid #f0f0f0;
      position:sticky;bottom:0;background:#fff;
    }
    #cvs-modal .hint{font-size:13px;color:#bbb;text-align:center;margin:0 0 12px;}
    #cvs-modal .hint.ok{color:#2a8a2a;}
    #cvs-modal .hint.err{color:#c00;}
    #cvs-modal .confirm{
      width:100%;padding:13px;border:none;border-radius:8px;
      font-size:15px;font-weight:500;cursor:pointer;
      background:#111;color:#fff;transition:background .12s;
    }
    #cvs-modal .confirm:disabled{background:#ccc;cursor:not-allowed;}
    #cvs-modal .confirm:not(:disabled):hover{background:#333;}
  `;
  document.head.appendChild(s);
}

// ─── 顯示規格選擇彈窗 ────────────────────────────────────────────────────────
// opts.mode：'buy'=立即下單(選好後跳轉 cod-checkout) | 'add'=加入購物車(選好後留在頁面)
function showVariantModal(productForm, productInfo, opts) {
  const mode = (opts && opts.mode) || 'buy';
  const confirmLabel = mode === 'add' ? '加入購物車' : '選好了，前往下單';
  document.getElementById('cvs-modal')?.remove();
  injectStyles();

  const variantPicker = document.querySelector('variant-picker');
  if (!variantPicker) return;
  const fieldsets = Array.from(variantPicker.querySelectorAll('fieldset'));
  if (!fieldsets.length) return;

  // 確保 variant 資料已載入
  loadVariants();

  // 建立彈窗 HTML，每個按鈕存好 option value（data-val）
  const grpsHtml = fieldsets.map((fs, gi) => {
    const legend = fs.querySelector('legend');
    const title = legend ? legend.textContent.trim().replace(/\s*:.*/, '') : `規格 ${gi + 1}`;
    const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
    const btnsHtml = inputs.map(inp => {
      const lbl = document.querySelector(`label[for="${inp.id}"]`);
      const text = lbl ? lbl.textContent.trim() : (inp.value || inp.id);
      // 也把 Liquid 嵌入的 data-variant-id 存到按鈕（單規格商品直接用）
      const directVid = inp.dataset.variantId || '';
      return `<button type="button" class="opt${inp.disabled ? '" disabled' : '"'}
        data-g="${gi}"
        data-iid="${inp.id}"
        data-val="${(inp.value || '').replace(/"/g, '&quot;')}"
        data-vid="${directVid}"
      >${text}</button>`;
    }).join('');
    return `<div class="grp"${fs.hasAttribute('data-sale-single-value') ? ' hidden style="display:none"' : ''}><p class="grp-lbl">${title}</p><div class="opts">${btnsHtml}</div></div>`;
  }).join('');

  const modal = document.createElement('div');
  modal.id = 'cvs-modal';
  modal.innerHTML = `
    <div class="box">
      <div class="hd">
        <span class="hd-title">請選擇商品規格</span>
        <button type="button" class="hd-close">✕</button>
      </div>
      <div class="bd">${grpsHtml}</div>
      <div class="ft">
        <p class="hint" id="cvs-hint">請選擇所有規格</p>
        <button type="button" class="confirm" id="cvs-confirm" disabled>${confirmLabel}</button>
      </div>
    </div>`;
  document.body.appendChild(modal);

  // 追蹤每組已選的 { value, directVid }
  const selectedValues = {}; // groupIndex → optionValue
  const selectedVids = {};   // groupIndex → variantId（單規格直接用）
  const total = fieldsets.length;
  const allDone = () => Object.keys(selectedValues).length === total;
  const close = () => document.getElementById('cvs-modal')?.remove();

  const updateState = () => {
    const hint = document.getElementById('cvs-hint');
    const btn = document.getElementById('cvs-confirm');
    if (!hint || !btn) return;
    if (allDone()) {
      hint.textContent = '規格已選好，點擊下方按鈕下單';
      hint.className = 'hint ok';
      btn.removeAttribute('disabled');
    } else {
      hint.textContent = `還有 ${total - Object.keys(selectedValues).length} 個規格未選擇`;
      hint.className = 'hint';
      btn.setAttribute('disabled', '');
    }
  };

  // M73: managed products bind every option (a greyed one can become selectable later) and check disabled on click.
  const hsCombo = hsComboModal(modal, selectedValues, selectedVids);
  modal.querySelectorAll(hsCombo ? '.opt' : '.opt:not([disabled])').forEach(btn => {
    btn.addEventListener('click', function () {
      if (this.disabled) return;
      const g = this.dataset.g;
      modal.querySelectorAll(`.opt[data-g="${g}"]`).forEach(b => b.classList.remove('on'));
      this.classList.add('on');
      selectedValues[g] = this.dataset.val;
      if (this.dataset.vid) selectedVids[g] = this.dataset.vid;
      if (hsCombo) hsCombo.after(Number(g));
      updateState();
    });
  });

  modal.querySelector('.hd-close').addEventListener('click', close);
  modal.addEventListener('click', e => { if (e.target === modal) close(); });

  const confirmBtn = document.getElementById('cvs-confirm');
  confirmBtn.addEventListener('click', () => {
    if (!allDone()) return;

    const qi = productForm.querySelector('input[name="quantity"]') ||
      productForm.querySelector('quantity-selector input');
    const quantity = parseInt(qi?.value) || 1;

    // 策略 1：從嵌入的 JSON 找 variant
    let variant = findVariantByValues(selectedValues);

    // 策略 2（單規格商品）：直接用按鈕上 data-variant-id
    if (!variant && total === 1 && selectedVids['0']) {
      const directVid = selectedVids['0'];
      const allVariants = loadVariants();
      variant = allVariants?.find(v => String(v.id) === String(directVid));
      if (!variant) {
        // P0f: 即使 fallback 也保留 sku 欄位（goToCheckout 會反查兜底）
        variant = { id: directVid, title: selectedValues['0'] || '', available: true, sku: '' };
      }
    }

    if (!variant) {
      const hint = document.getElementById('cvs-hint');
      if (hint) {
        hint.textContent = '⚠ 找不到對應規格，請重新選擇';
        hint.className = 'hint err';
      }
      console.error('[COD] Could not resolve variant. Selected values:', selectedValues, 'Variants:', loadVariants());
      return;
    }

    // 傳入 confirmBtn 讓流程可以顯示 loading 狀態
    if (mode === 'add') {
      addToCartStayOnPage(variant, quantity, productInfo, confirmBtn);
    } else {
      goToCheckout(variant, quantity, productInfo, confirmBtn);
    }
  });

  // 同步頁面上已勾選狀態
  fieldsets.forEach((fs, gi) => {
    let checked = fs.querySelector('input[type="radio"]:checked');
    const internal = fs.hasAttribute('data-sale-single-value') ? fs.querySelector('input[type="radio"]') : null;
    if (checked || internal) {
      checked = checked || internal;
      const chosen = checked;
      selectedValues[gi] = chosen.value;
      if (chosen.dataset.variantId) selectedVids[gi] = chosen.dataset.variantId;
      selectedValues[gi] = checked.value;
      if (checked.dataset.variantId) selectedVids[gi] = checked.dataset.variantId;
      const btn = modal.querySelector(`.opt[data-iid="${checked.id}"]`);
      if (btn) btn.classList.add('on');
    }
  });
  if (hsCombo) hsCombo.refresh();
  updateState();
}

// ─── 取得商品基本資訊 ────────────────────────────────────────────────────────
function getProductInfo(productForm) {
  const btn = productForm.querySelector('[data-product-id]') || document.querySelector('[data-product-id]');
  return {
    id: btn?.dataset.productId,
    title: btn?.dataset.productTitle,
    url: btn?.dataset.productUrl,
    handle: btn?.dataset.productHandle
  };
}

// ─── 重設規格選擇（不預選 SKU） ──────────────────────────────────────────────
function resetVariantSelection() {
  // Managed products initialize an atomic safe selection; never erase it later.
  if (document.querySelector('variant-picker[data-sale-managed="true"]')) return;
  const vp = document.querySelector('variant-picker');
  if (vp) {
    vp.querySelectorAll('input[type="radio"]').forEach(i => { i.checked = false; });
    vp.querySelectorAll('select option[selected]').forEach(o => o.removeAttribute('selected'));
  }
  // M84：只有 1 個款式的商品沒有可選的款式，也沒有預設款機制；保留伺服器給的 variantId，運送小卡才會顯示 7-11 那行。
  // 多款式（或讀不到款式資料）照舊清空，由預設款機制或客人自己選。
  const allVariants = loadVariants();
  const singleVariant = Array.isArray(allVariants) && allVariants.length === 1;
  if (!singleVariant) document.querySelectorAll('product-form-component').forEach(f => {
    const vi = f.querySelector('input[ref="variantId"]');
    if (vi) vi.value = '';
  });
  const mg = document.querySelector('media-gallery');
  if (mg) {
    try { mg.refs?.slideshow?.select(0, undefined, { animate: false }); } catch (_) {}
    try {
      const ss = mg.querySelector('slideshow-component');
      if (ss?.select) ss.select(0, undefined, { animate: false });
    } catch (_) {}
  }
}

// ─── 攔截所有購買按鈕 ────────────────────────────────────────────────────────
function interceptButtons() {
  document.querySelectorAll('product-form-component').forEach(productForm => {
    const info = getProductInfo(productForm);
    const guardRetired = e => {
      const picker = document.querySelector('variant-picker[data-sale-managed="true"]');
      if (!picker || String(info.id) !== picker.dataset.productId) return false;
      const vi = productForm.querySelector('input[ref="variantId"]');
      if (!vi || !vi.value.trim() || homieSaleAllowed(vi.value.trim())) return false;
      e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
      homieSaleNotice();
      return true;
    };
    productForm.addEventListener('submit', guardRetired, { capture: true });

    const handleClick = (e) => {
      if (guardRetired(e)) return;
      e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
      // 2026-10-13 M26 鞋拔預選組合：有效預選組合存在 → 先開組合彈窗（不走單款加購）。
      // 組合 JS 沒載入、規則無效、任何例外 → 照原本單款流程。
      try { if (window.HomieBundlePreselect && window.HomieBundlePreselect.isActive() && window.HomieBundlePreselect.open() === true) return; } catch (_) {}
      const vi = productForm.querySelector('input[ref="variantId"]');
      if (!vi || !vi.value.trim()) {
        showVariantModal(productForm, info);
      } else {
        const variants = loadVariants();
        // P0f: 即使 fallback 也保留 sku 欄位（goToCheckout 會反查兜底）
        const variant = variants?.find(v => String(v.id) === vi.value.trim()) || { id: vi.value.trim(), sku: '' };
        const qi = productForm.querySelector('input[name="quantity"]') ||
          productForm.querySelector('quantity-selector input');
        goToCheckout(variant, parseInt(qi?.value) || 1, info, null);
      }
    };

    // P0h (2026-05-06): 只攔截「立即下單」按鈕（有 data-product-id），
    //   「加入購物車」按鈕完全不攔，讓 Shopify ProductFormComponent.handleSubmit 處理
    //   - 99% 情況自動 ajax 加購 + floating-cart.js fetch 攔截自動更新徽章
    //   - 1% SSR 失敗 fallback 跳 /cart 頁（Shopify 預設 UI，仍能下單）
    //   - 不寫 manual fallback 避免「Shopify 加 1 + 我們加 1 = 加倍」bug
    //     （Root cause：AddToCartComponent.handleClick 不 preventDefault click，
    //      實際攔截在 ProductFormComponent.handleSubmit 攔 form submit event；
    //      用 e.defaultPrevented 判定 Shopify 是否接手永遠 false → 100% 誤觸發）
    productForm.querySelectorAll('[data-product-id]').forEach(btn => {
      btn.addEventListener('click', handleClick, { capture: true });
    });

    // (2026-06-24) 原生「加入購物車」鈕守門 —
    //   resetVariantSelection() 會把 variantId 清空；若使用者未選規格直接按原生加入購物車，
    //   會送空 id 給 /cart/add.js → Shopify 退件 → 跳「Cart Error」紅字。
    //   守門：capture 階段攔截，「未選規格」才接管（跳選規格弹窗 add 模式 → 選好走受控加購）；
    //   「已選規格」完全不攔，交 Shopify 原生 handleSubmit（Pixel/floating-cart 全原生，零影響，
    //   也不會出現「Shopify 加 1 + 我們加 1」加倍問題）。
    productForm.addEventListener('click', (e) => {
      const addBtn = e.target && e.target.closest && e.target.closest('[ref="addToCartButton"]');
      if (!addBtn || !productForm.contains(addBtn)) return;
      if (guardRetired(e)) return;
      const vi = productForm.querySelector('input[ref="variantId"]');
      if (!vi || !vi.value.trim()) {
        e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
        showVariantModal(productForm, info, { mode: 'add' });
      }
      // 已選規格 → 不動作，讓原生流程正常加購
    }, { capture: true });
  });
}

// ─── 讓按鈕在切換規格時不要灰掉 ─────────────────────────────────────────────
// Shopify 主題在選規格時會先把按鈕 disabled，等 fetch 回來才 re-enable（約 1-2 秒）。
// 因為 cod-product.js 所有 variant 資料都已嵌在頁面內不需要 fetch，所以在
// 'variant:selected' 事件觸發後立刻把按鈕重新打開，消除那段延遲感。
function keepAddToCartAlwaysEnabled() {
  document.addEventListener('variant:selected', () => {
    // 用 setTimeout(0) 讓主題的 disable 先執行，再立即覆蓋回去
    setTimeout(() => {
      document.querySelectorAll('[ref="addToCartButton"]').forEach(btn => {
        // Never override Horizon inventory/availability on retirement-managed products.
        const form = btn.closest('product-form-component');
        const managed = document.querySelector('variant-picker[data-sale-managed="true"]');
        if (managed && form && String(getProductInfo(form).id) === managed.dataset.productId) return;
        if (btn instanceof HTMLButtonElement && btn.disabled) {
          btn.disabled = false;
        }
      });
    }, 0);
  });
}

// Keep the managed COD button in sync with the confirmed canonical form ID.
function syncManagedCodButton() {
  const picker = document.querySelector('variant-picker[data-sale-managed="true"]');
  if (!picker) return;
  document.querySelectorAll('product-form-component').forEach(form => {
    const button = form.querySelector('[data-m65-cod]');
    if (!button || String(button.dataset.productId) !== picker.dataset.productId) return;
    const id = form.querySelector('input[name="id"]')?.value;
    const allowed = !!id && homieSaleAllowed(id);
    button.disabled = !allowed;
    button.setAttribute('aria-disabled', String(!allowed));
  });
}
document.addEventListener('variant:update', () => setTimeout(syncManagedCodButton, 0));
document.addEventListener('hs:default-selected', syncManagedCodButton);
// ─── 初始化 ──────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  console.log('[COD] Product script loaded');
  loadVariants(); // 預先載入
  keepAddToCartAlwaysEnabled(); // 消除切換規格時的按鈕灰色延遲
  requestAnimationFrame(() => requestAnimationFrame(() => {
    resetVariantSelection();
    interceptButtons();
    syncManagedCodButton();
    window.__hsVariantResetComplete = true;
  }));
});


/**
 * Append-only product-page mobile proxy. No cart, tracking or checkout logic.
 * Source contracts:
 * - cod-product.js intercepts product-form-component [data-product-id].
 * - bundle selector preselectRule() reads the trigger's data-bundle-rule;
 *   rule.price is TWD (not variant cents), rendered directly as NT$.
 * - Checkout mobilebar supplies the white surface, border, shadow and spacing.
 */
;(function () {
  'use strict';

  var KEY = '__hsProductMobileBar20261015';
  if (window[KEY]) return;
  window[KEY] = true;

  function init() {
    if (!document.body || !window.IntersectionObserver || !window.MutationObserver) return;
    var BAR = 'hs-product-mobile-bar-20261015';
    var SPACE = BAR + '-spacer';
    if (document.getElementById(BAR)) return;

    var media = window.matchMedia('(max-width: 749px)');
    var original = null;
    var intersection = null;
    var observed = false;
    var intersects = true;
    var frame = 0;
    var reservedHeight = '';

    var style = document.createElement('style');
    style.id = BAR + '-styles';
    style.textContent = [
      '#' + BAR + ',#' + SPACE + '{display:none!important;}',
      '@media screen and (max-width:749px){',
      '#' + BAR + ':not([hidden]){display:flex!important;gap:10px;align-items:center;position:fixed;',
      'inset:auto 0 0;z-index:900;box-sizing:border-box;width:100%;margin:0;',
      'padding:8px 12px calc(8px + env(safe-area-inset-bottom,0px));',
      'background:#fff;border:0;border-top:1px solid #e5e5e5;',
      'box-shadow:0 -2px 10px rgba(0,0,0,.08);isolation:isolate;}',
      '#' + BAR + ' button{display:flex;align-items:center;justify-content:center;flex:1;box-sizing:border-box;min-width:0;',
      'height:52px;min-height:52px;margin:0;padding:0;border:0;border-radius:12px;',
      'background:#15803D;color:#fff;font:700 17px/1 var(--font-body--family,sans-serif);',
      'letter-spacing:.5px;text-align:center;white-space:nowrap;overflow-wrap:normal;cursor:pointer;',
      'touch-action:manipulation;line-height:1;vertical-align:middle;transform:none;}',
      '#' + BAR + ' .hs-mobile-order-label{display:block;line-height:1;padding:0;margin:0;text-align:center;}',
      '#' + BAR + ' button:active:not(:disabled){background:#166534;}',
      '#' + BAR + ' button:disabled{opacity:.55;cursor:not-allowed;}',
      '#' + BAR + ' button:focus-visible{outline:2px solid #222;outline-offset:3px;}',
      '#' + BAR + ' .hs-product-line-service{display:flex;flex:none;align-items:center;justify-content:center;',
      'box-sizing:border-box;width:52px;min-width:52px;height:52px;min-height:52px;margin:0;padding:0;',
      'border:0;border-radius:12px;background:#06C755;color:#176b38;text-decoration:none;',
      'font:600 12px/1 var(--font-body--family,sans-serif);white-space:nowrap;touch-action:manipulation;line-height:1;text-align:center;vertical-align:middle;transform:none;}',
      '#' + BAR + ' .hs-product-line-service svg{display:block;flex:none;width:40px;height:38px;margin:0;}',
      '#' + BAR + ' .hs-product-line-service:focus-visible{outline:2px solid #176b38;outline-offset:3px;}',
      '#' + SPACE + ':not([hidden]){display:block!important;box-sizing:border-box;',
      'height:calc(69px + env(safe-area-inset-bottom,0px));min-height:0;',
      'flex:none;width:100%;margin:0;padding:0;border:0;pointer-events:none;}',
      '}'
    ].join('');
    document.head.appendChild(style);

    var bar = document.createElement('div');
    bar.id = BAR;
    bar.hidden = true; // Never show until the original's first IO result.
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', '商品下單');
    var button = document.createElement('button');
    button.type = 'button';
    button.disabled = true;
    var orderLabel = document.createElement('span');
    orderLabel.className = 'hs-mobile-order-label';
    orderLabel.textContent = '立即下單';
    button.appendChild(orderLabel);
    bar.appendChild(button);
    var service = document.createElement('a');
    service.className = 'hs-product-line-service';
    service.href = 'https://lin.ee/zCTGYQl';
    service.target = '_blank';
    service.rel = 'noopener';
    service.setAttribute('aria-label', 'LINE 客服');
    service.setAttribute('title', 'LINE 客服');
    service.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 100 94" shape-rendering="geometricPrecision" aria-hidden="true" focusable="false"><path d="M50 6C25 6 6 21 6 40c0 17 15 31 36 34l-3 13c-.4 2 1.6 3.2 3.2 2.1L62 75C80 71 94 57 94 40 94 21 75 6 50 6z" fill="#fff"/><path data-line-glyph d="M22.5 50.1L34.14 50.1L34.14 46.89L26.34 46.89L26.34 30.9L22.5 30.9ZM37.48 50.1L41.32 50.1L41.32 30.9L37.48 30.9ZM46.03 50.1L49.69 50.1L49.69 42.41C49.69 40.2 49.35 37.79 49.2 35.72L49.33 35.72L51.27 39.84L56.79 50.1L60.7 50.1L60.7 30.9L57.08 30.9L57.08 38.57C57.08 40.75 57.39 43.29 57.59 45.28L57.46 45.28L55.52 41.14L49.95 30.9L46.03 30.9ZM65.45 50.1L77.5 50.1L77.5 46.89L69.28 46.89L69.28 41.76L76 41.76L76 38.54L69.28 38.54L69.28 34.11L77.21 34.11L77.21 30.9L65.45 30.9Z" fill="#06C755"/></svg>';
    // Keep native navigation; never proxy a service click to the order action.
    // No preventDefault, purchasing attributes, custom pixel or Clarity events.
    service.addEventListener('click', function (event) { event.stopPropagation(); });
    bar.appendChild(service);
    var spacer = document.createElement('div');
    spacer.id = SPACE;
    spacer.hidden = true;
    spacer.setAttribute('aria-hidden', 'true');
    // Body siblings: no product container clipping or existing padding changes.
    document.body.appendChild(spacer);
    document.body.appendChild(bar);

    function owned(node) {
      return node && (node === style || node === spacer || node === bar || bar.contains(node));
    }

    function rendered(element) {
      if (!element || !element.isConnected || element.closest('[hidden], [inert]')) return false;
      for (var node = element; node && node.nodeType === 1; node = node.parentElement) {
        var css = window.getComputedStyle(node);
        if (css.display === 'none' || css.visibility === 'hidden' ||
            css.visibility === 'collapse' || Number(css.opacity) === 0) return false;
      }
      var rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    function inViewport(element) {
      if (!rendered(element)) return false;
      var rect = element.getBoundingClientRect();
      return rect.bottom > 0 && rect.right > 0 &&
        rect.top < document.documentElement.clientHeight &&
        rect.left < document.documentElement.clientWidth;
    }

    function findOriginal() {
      var candidates = document.querySelectorAll('product-form-component [data-product-id]');
      var fallback = null;
      for (var i = 0; i < candidates.length; i++) {
        var item = candidates[i];
        if (owned(item) || typeof item.click !== 'function' ||
            item.closest('dialog, [role="dialog"], quick-add-dialog, cart-drawer, .drawer, [data-drawer]')) continue;
        if (!fallback) fallback = item;
        if (rendered(item)) return item;
      }
      return fallback;
    }

    function bindOriginal(next) {
      if (next === original) return;
      if (intersection) intersection.disconnect();
      original = next;
      observed = false;
      intersects = true;
      bar.hidden = true;
      if (!next) return;
      // A fresh observer/closure discards queued callbacks for replaced originals.
      var observer = new IntersectionObserver(function (entries) {
        if (intersection !== observer || original !== next) return;
        entries.forEach(function (entry) {
          if (entry.target !== original) return;
          observed = true;
          intersects = entry.isIntersecting;
        });
        schedule();
      }, { root: null, rootMargin: '0px', threshold: 0 });
      intersection = observer;
      observer.observe(next);
    }

    function blockedByOverlay() {
      var overlays = document.querySelectorAll([
        '#bundle-modal', '#cvs-modal', 'dialog[open]',
        '[role="dialog"]',
        '.drawer', '.cart-drawer', '.menu-drawer', '[data-drawer]', 'cart-drawer', 'menu-drawer',
        '.drawer__inner', '.drawer__panel', '[part="drawer"]'
      ].join(','));
      for (var i = 0; i < overlays.length; i++) {
        var element = overlays[i];
        // Native closed dialogs remain nonblocking, including their drawer wrappers.
        if (element.tagName === 'DIALOG' && !element.open) continue;
        if (element.getAttribute('aria-hidden') === 'true') continue;
        if (inViewport(element)) return true;
      }
      return false;
    }

    function stateOf(element) {
      var busy = false;
      var disabled = !element;
      for (var node = element; node && node !== document.body; node = node.parentElement) {
        busy = busy || node.getAttribute('aria-busy') === 'true' ||
          node.classList.contains('loading') || node.classList.contains('is-loading') ||
          node.hasAttribute('data-loading');
        disabled = disabled || node.getAttribute('aria-disabled') === 'true' ||
          node.hasAttribute('inert');
      }
      return {
        busy: busy,
        disabled: disabled || busy || !!(element && (element.disabled || element.matches(':disabled')))
      };
    }

    function hsFmtTwd(text) {
      var m = String(text).match(/\d[\d,]*(?:\.\d+)?/);
      if (!m) return text;
      var n = Number(m[0].replace(/,/g, ''));
      return isFinite(n) ? 'NT$' + n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : text;
    }

    function currentPrice() {
      // 2026-10-28 M68：三格選項開啟時，底部固定鈕顯示選中那一格的金額（window.HomieBundleQty 由 product-bundle-selector.js 提供）。
      // 沒載入、沒有三格、任何例外 → 照原本讀主價格。
      try {
        var qtyLabel = window.HomieBundleQty && window.HomieBundleQty.label();
        if (qtyLabel) return qtyLabel;
      } catch (_) { /* fall through */ }
      // Same active-card rule validation as preselectRule(), plus a valid price.
      var card = document.querySelector('.bundle-promo-card[data-bundle-preselect="1"]');
      var trigger = card && card.querySelector('[data-bundle-trigger]');
      if (trigger) {
        try {
          var rule = JSON.parse(trigger.getAttribute('data-bundle-rule'));
          if (rule && Array.isArray(rule.requires) && rule.requires.length &&
              Number(rule.quantity) >= 2 &&
              (typeof rule.price === 'number' || typeof rule.price === 'string' && rule.price.trim() !== '') &&
              Number.isFinite(Number(rule.price)) && Number(rule.price) >= 0) {
            return 'NT$' + Number(rule.price).toLocaleString('zh-TW', { maximumFractionDigits: 2 });
          }
        } catch (_) { /* Invalid preselect falls back to the primary current price. */ }
      }
      if (!original) return '';
      var form = original.closest('product-form-component');
      var scope = original.closest('.product-information, [data-product-information], .shopify-section') ||
        document.querySelector('main') || form;
      var prices = scope.querySelectorAll('product-price');
      for (var i = 0; i < prices.length; i++) {
        var price = prices[i];
        if (price.closest('product-card, .product-card, .recommendations, dialog, .drawer')) continue;
        var container = price.querySelector('[ref="priceContainer"]') || price;
        // Never read the container's combined text: that includes compare-at and labels.
        var values = container.querySelectorAll(
          '.price-item--sale, .price__sale .price-item:not(.price-item--regular), ' +
          '[data-current-price], .price:not(.compare-at-price), .price-item--regular'
        );
        for (var j = 0; j < values.length; j++) {
          var value = values[j];
          if (value.closest('s, del, .compare-at-price, .price__compare, .price-item--compare, [data-compare-price]') ||
              value.querySelector('s, del, .compare-at-price, .price__compare, [data-compare-price]') ||
              !rendered(value)) continue;
          var text = value.textContent.replace(/\s+/g, ' ').trim();
          if (/\d/.test(text)) return hsFmtTwd(text);
        }
      }
      return '';
    }

    function reserveSpace() {
      // Reserve throughout the mobile product view, even when IO hides the bar.
      // Toggling the spacer with intersection would cause bottom-of-page oscillation.
      spacer.hidden = !media.matches || !original;
      if (!bar.hidden) {
        var height = Math.ceil(bar.getBoundingClientRect().height);
        var value = height > 0 ? height + 'px' : '';
        if (value && value !== reservedHeight) {
          reservedHeight = value;
          spacer.style.height = value;
        }
      }
    }

    function sync() {
      frame = 0;
      bindOriginal(findOriginal());
      var state = stateOf(original);
      var label = currentPrice();
      label = (label ? label + ' · ' : '') + '立即下單';
      if (orderLabel.textContent !== label) orderLabel.textContent = label;
      if (button.disabled !== state.disabled) button.disabled = state.disabled;
      var busy = state.busy ? 'true' : 'false';
      if (button.getAttribute('aria-busy') !== busy) button.setAttribute('aria-busy', busy);
      var disabled = state.disabled ? 'true' : 'false';
      if (button.getAttribute('aria-disabled') !== disabled) button.setAttribute('aria-disabled', disabled);
      var show = media.matches && original && observed && !intersects &&
        rendered(original) && !blockedByOverlay();
      bar.hidden = !show;
      reserveSpace();
    }

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(sync);
    }

    button.addEventListener('click', function () {
      // Resolve again, never invoke a detached/replaced or newly blocked control.
      var next = findOriginal();
      if (next !== original) {
        bindOriginal(next);
        schedule();
        return;
      }
      if (!media.matches || bar.hidden || !observed || intersects ||
          !original || !rendered(original) || inViewport(original) ||
          stateOf(original).disabled || blockedByOverlay()) {
        schedule();
        return;
      }
      original.click(); // The only purchasing action: delegate to existing handlers.
      sync(); // Synchronous modal/disabled changes are immediately reflected.
    });

    var mutations = new MutationObserver(function (records) {
      if (records.some(function (record) { return !owned(record.target); })) schedule();
    });
    mutations.observe(document.body, {
      subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: [
        'disabled', 'aria-disabled', 'aria-busy', 'aria-hidden', 'aria-modal',
        'hidden', 'inert', 'open', 'class', 'style', 'data-loading',
        'data-product-id', 'data-bundle-preselect', 'data-bundle-rule'
      ]
    });
    if (window.ResizeObserver) {
      new ResizeObserver(schedule).observe(bar);
    }
    [
      'variant:selected', 'variant:change', 'variant:changed', 'variant:update',
      'product:variant-change', 'shopify:section:load', 'shopify:section:unload',
      'hs:preselect-released', 'hs:bundle-modal-open', 'change', 'toggle',
      'close', 'transitionend', 'animationend'
    ].forEach(function (name) { document.addEventListener(name, schedule, true); });
    if (media.addEventListener) media.addEventListener('change', schedule);
    else media.addListener(schedule);
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('pageshow', schedule);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', schedule, { passive: true });
      window.visualViewport.addEventListener('scroll', schedule, { passive: true });
    }
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
